import '../../../i18n';
import {useTranslation} from 'react-i18next';
import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import MediaToolsPanel from './MediaToolsPanel';
import { UPLOAD_URLMETA_MEDIA } from '../../../gql-operations/mutations/message/upload-urlmeta-media';
import { UPLOAD_VIDEO_FROM_URL } from '../../../gql-operations/mutations/message/upload-video-from-url';
import { EXTRACT_URL_MEDIA } from '../../../gql-operations/mutations/message/extract-url-media';
import { EXTRACT_URL_TRANSCRIPT } from '../../../gql-operations/mutations/message/extract-url-transcript';
import { SCAN_URL_VIDEO_TEXT } from '../../../gql-operations/mutations/message/scan-url-video-text';
import { DOWNLOAD_URL_INFO_DUMP } from '../../../gql-operations/mutations/message/download-url-info-dump';
import { DOWNLOAD_REDDIT_JSON_DUMP } from '../../../gql-operations/mutations/message/download-reddit-json-dump';
import { PROBE_URL_DIRECT_DOWNLOADABILITY } from '../../../gql-operations/mutations/message/probe-url-direct-downloadability';
import { CREATE_MESSAGE } from '../../../gql-operations/mutations/message/create-message';
import { PULL_URL_MARKDOWN } from '../../../gql-operations/mutations/message/pull-url-markdown';
import { GENERATE_URL_PDF } from '../../../gql-operations/mutations/message/generate-url-pdf';
import { QUERY } from '../../../gql-operations';
import { GET_FEATURE_FLAG } from '../../../gql-operations/featureFlags';
import useDownloadUrlMetaVideo from '../../../hooks/useDownloadUrlMetaVideo';
import useUpdateMessageMutation from '../../../hooks/useUpdateMessageMutation';
import useNotificationSystem from '../../../components/Notifications/useNotificationSystem';
import customAnalytics from '../../../util/custom-analytics';
import { getUrlMetaVideoSource, isUrlMetaVideo } from '../../../util/url-meta-media';
import {
    evaluateFeatureFlagForUser,
    mapTranscriptionErrorToUiAction
} from '../../../util/featureFlagAccess';
import {
    FEATURE_UPGRADE_MODAL_PRESET,
    openFeatureUpgradeModal
} from '../../../util/featureUpgradeModalEvents';
import {
    getAudioProcessingStatusText,
    isAudioProcessingActive
} from '../../../util/audioProcessingStatus';

const URL_META_INPUT_KEYS = new Set([
    'author',
    'date',
    'description',
    'article_url',
    'article_body',
    'article_pull_provider',
    'image',
    'image_asset_id',
    'gallery_images',
    'gallery',
    'media_url',
    'media_name',
    'media_asset_id',
    'media_type',
    'media_width',
    'media_height',
    'media_duration',
    'media_has_audio',
    'media_audio_url',
    'media_audio_name',
    'media_audio_asset_id',
    'media_hls_url',
    'logo',
    'publisher',
    'title',
    'url',
    'public_id',
    'image_name',
    'name',
    'displayName',
    'type',
    'format',
    'size',
    'aspect_ratio',
    'width',
    'embed',
    'author_image',
    'author_image_public_id',
    'author_image_name',
    'author_image_size',
    'author_image_format',
    'author_image_aspect_ratio',
    'author_image_width',
    'author_image_original'
]);

function sanitizeEmbedInput(embed) {
    if (!embed || typeof embed !== 'object') return undefined;

    const sanitized = {};

    if (typeof embed.html === 'string') {
        sanitized.html = embed.html;
    }

    if (embed.script && typeof embed.script === 'object') {
        const script = {};
        if (typeof embed.script.src === 'string') {
            script.src = embed.script.src;
        }
        if (typeof embed.script.async === 'boolean') {
            script.async = embed.script.async;
        }
        if (typeof embed.script.charset === 'string') {
            script.charset = embed.script.charset;
        }
        if (Object.keys(script).length) {
            sanitized.script = script;
        }
    }

    return Object.keys(sanitized).length ? sanitized : undefined;
}

function sanitizeUrlMetaInput(meta) {
    if (!meta || typeof meta !== 'object') return null;

    const sanitized = {};
    for (const key of URL_META_INPUT_KEYS) {
        if (!Object.prototype.hasOwnProperty.call(meta, key)) {
            continue;
        }
        if (key === 'embed') {
            const cleanedEmbed = sanitizeEmbedInput(meta.embed);
            if (cleanedEmbed) {
                sanitized.embed = cleanedEmbed;
            }
            continue;
        }
        const value = meta[key];
        if (value !== undefined) {
            sanitized[key] = value;
        }
    }

    return Object.keys(sanitized).length ? sanitized : null;
}

const SUPPORTED_SOCIAL_HOSTS = new Set([
    'x.com',
    'www.x.com',
    'mobile.x.com',
    'twitter.com',
    'www.twitter.com',
    'mobile.twitter.com'
]);

// These URLs are posts/videos, not read-later pages. Their logged-out HTML is
// frequently a sign-in shell, so never offer it as an article to save.
const ARTICLE_PULL_BLOCKED_SOCIAL_HOSTS = new Set([
    'threads.com',
    'www.threads.com',
    'threads.net',
    'www.threads.net',
    'instagram.com',
    'www.instagram.com',
    'instagr.am',
    'www.instagr.am',
    'tiktok.com',
    'www.tiktok.com',
    'm.tiktok.com',
    'vt.tiktok.com'
]);

const YTDLP_SUPPORTED_HOSTS = new Set([
    'youtube.com',
    'www.youtube.com',
    'm.youtube.com',
    'music.youtube.com',
    'youtu.be',
    'threads.com',
    'www.threads.com',
    'threads.net',
    'www.threads.net',
    'instagram.com',
    'www.instagram.com',
    'instagr.am',
    'www.instagr.am',
    'vimeo.com',
    'www.vimeo.com',
    'player.vimeo.com',
    'x.com',
    'www.x.com',
    'mobile.x.com',
    'twitter.com',
    'www.twitter.com',
    'mobile.twitter.com',
    'tiktok.com',
    'www.tiktok.com',
    'm.tiktok.com',
    'vt.tiktok.com',
    'reddit.com',
    'www.reddit.com',
    'old.reddit.com',
    'm.reddit.com',
    'redd.it',
    'v.redd.it',
    'twitch.tv',
    'www.twitch.tv',
    'm.twitch.tv',
    'clips.twitch.tv',
    'dailymotion.com',
    'www.dailymotion.com',
    'dai.ly',
    'soundcloud.com',
    'www.soundcloud.com',
    'm.soundcloud.com',
    'facebook.com',
    'www.facebook.com',
    'm.facebook.com',
    'fb.watch',
    'loom.com',
    'www.loom.com',
    'archive.org',
    'www.archive.org',
    'web.archive.org'
]);

const YOUTUBE_HOSTS = new Set([
    'youtube.com',
    'www.youtube.com',
    'm.youtube.com',
    'music.youtube.com',
    'youtu.be'
]);

const URL_MEDIA_TOOLS_YTDLP_FLAG = 'url_media_tools_ytdlp';
const URL_VIDEO_TEXT_SCAN_FLAG = 'url_video_text_scan';
const REDDIT_JSON_SAVE_FLAG = 'reddit_json_save';
const URL_TRANSCRIPT_FLAG = 'url_transcript';

const MARKDOWN_PROVIDERS = {
    MICROLINK: 'microlink'
};

const buildUrlMetaWithArticleBody = ({ message, metaData, urlMetaIndex, pageUrl, markdown, provider }) => {
    const existingUrlMeta = Array.isArray(message?.urlMeta) && message.urlMeta.length
        ? message.urlMeta
        : [metaData || {}];
    const safeIndex = Number.isInteger(urlMetaIndex) && urlMetaIndex >= 0 ? urlMetaIndex : 0;
    const nextUrlMeta = existingUrlMeta.map((meta, index) => (
        index === safeIndex
            ? {
                ...(meta || {}),
                article_url: meta?.article_url || pageUrl,
                article_body: markdown,
                article_pull_provider: provider
            }
            : meta
    ));

    if (!nextUrlMeta[safeIndex]) {
        nextUrlMeta[safeIndex] = {
            ...(metaData || {}),
            url: pageUrl,
            article_url: pageUrl,
            article_body: markdown,
            article_pull_provider: provider
        };
    }

    return nextUrlMeta;
};

function isAbortLikeError(error) {
    const message = String(
        error?.message ||
        error?.networkError?.message ||
        error?.graphQLErrors?.[0]?.message ||
        ''
    ).toLowerCase();

    const name = String(
        error?.name ||
        error?.networkError?.name ||
        ''
    ).toLowerCase();

    return (
        name === 'aborterror' ||
        message.includes('aborted') ||
        message.includes('abort') ||
        message.includes('cancelled') ||
        message.includes('canceled')
    );
}

function sanitizeClientFacingLinkToolError(rawMessage, fallbackMessage) {
    const normalizedRaw = String(rawMessage || '').replace(/\r/g, '\n').trim();
    if (!normalizedRaw) {
        return fallbackMessage;
    }

    const firstLine = normalizedRaw
        .split('\n')
        .map((line) => line.trim())
        .find(Boolean) || '';
    const lower = `${normalizedRaw}\n${firstLine}`.toLowerCase();

    if (
        /^traceback/i.test(firstLine) ||
        lower.includes('traceback (most recent call last)') ||
        /yt-?dlp/i.test(lower) ||
        lower.includes('spawnargs=') ||
        lower.includes('site-packages')
    ) {
        return fallbackMessage;
    }

    return firstLine.slice(0, 240);
}

function resolveActionErrorMessage(error, fallbackMessage) {
    const graphQlMessage = error?.graphQLErrors?.[0]?.message;
    const networkGraphQlMessage = error?.networkError?.result?.errors?.[0]?.message;
    const topLevelMessage = error?.message;
    const rawMessage = graphQlMessage || networkGraphQlMessage || topLevelMessage || '';
    return sanitizeClientFacingLinkToolError(rawMessage, fallbackMessage);
}

function createClientRequestId(scope = 'link-action') {
    const normalizedScope = String(scope || 'link-action')
        .trim()
        .replace(/[^a-zA-Z0-9._:-]/g, '-')
        .slice(0, 40) || 'link-action';

    if (
        typeof window !== 'undefined' &&
        window.crypto &&
        typeof window.crypto.randomUUID === 'function'
    ) {
        return `${normalizedScope}:${window.crypto.randomUUID()}`;
    }

    return `${normalizedScope}:${Date.now().toString(36)}:${Math.random().toString(36).slice(2, 10)}`;
}

function parseUrl(urlValue) {
    if (!urlValue || typeof urlValue !== 'string') return null;
    const trimmed = urlValue.trim();
    if (!trimmed) return null;

    const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    try {
        return new URL(candidate);
    } catch {
        return null;
    }
}

function resolvePrimaryUrl({ metaData, message, urlMetaIndex }) {
    const candidates = [
        metaData?.url,
        message?.urlMeta?.[urlMetaIndex]?.url
    ];

    for (const candidate of candidates) {
        const parsed = parseUrl(candidate);
        if (!parsed) continue;
        return parsed.toString();
    }

    return null;
}

function resolveSupportedSocialUrl(primaryUrl) {
    const parsed = parseUrl(primaryUrl);
    if (!parsed) return null;
    return SUPPORTED_SOCIAL_HOSTS.has(parsed.hostname.toLowerCase()) ? parsed.toString() : null;
}

function isBlockedSocialArticleUrl(primaryUrl) {
    const parsed = parseUrl(primaryUrl);
    return !!parsed && ARTICLE_PULL_BLOCKED_SOCIAL_HOSTS.has(parsed.hostname.toLowerCase());
}

function resolveYtDlpSupportedUrl(primaryUrl) {
    const parsed = parseUrl(primaryUrl);
    if (!parsed) return null;
    const host = parsed.hostname.toLowerCase();
    if (!YTDLP_SUPPORTED_HOSTS.has(host)) {
        return null;
    }

    if (/^(?:www\.)?threads\.(?:com|net)$/.test(host) &&
        !/^\/(?:@[^/]+\/post|t)\/[\w-]+\/?$/.test(parsed.pathname)) return null;

    const isTikTokPhoto = (
        (host === 'tiktok.com' || host.endsWith('.tiktok.com')) &&
        /\/photo\/[^/]+/i.test(parsed.pathname)
    );

    if (isTikTokPhoto) {
        return null;
    }

    return parsed.toString();
}

function resolveYouTubeUrl(primaryUrl) {
    const parsed = parseUrl(primaryUrl);
    if (!parsed) return null;
    return YOUTUBE_HOSTS.has(parsed.hostname.toLowerCase()) ? parsed.toString() : null;
}

function hasSavedVideoMedia(meta) {
    if (!meta || typeof meta !== 'object') return false;

    const hasVideoCandidate = candidate => {
        if (!candidate || candidate.media_has_audio === false) return false;
        const mediaType = String(candidate.media_type || candidate.type || '').toLowerCase();
        const looksLikeVideo = mediaType === 'video' || mediaType.includes('video');
        const hasStoredVideo = Boolean(
            candidate.media_name ||
            candidate.media_asset_id ||
            candidate.sharedMediaAssetId ||
            candidate.shared_media_asset_id ||
            candidate.shared_asset_id ||
            candidate?.media?.shared_asset_id
        );
        const hasVideoSource = Boolean(candidate.media_url || candidate.media_cdn_url || candidate.media_hls_url);
        return looksLikeVideo || hasStoredVideo || hasVideoSource;
    };

    if (hasVideoCandidate(meta)) return true;
    return Array.isArray(meta.gallery) && meta.gallery.some(hasVideoCandidate);
}

function resolveRedditPostUrl(primaryUrl) {
    const parsed = parseUrl(primaryUrl);
    if (!parsed) return null;

    const host = parsed.hostname.toLowerCase();
    if (!['reddit.com', 'www.reddit.com', 'old.reddit.com', 'm.reddit.com'].includes(host)) {
        return null;
    }

    return /\/r\/[^/]+\/comments\/[^/]+/i.test(parsed.pathname) ? parsed.toString() : null;
}

function prependBodyBlock(existingBody, newBlock) {
    const nextBlock = (newBlock || '').trim();
    const currentBody = (existingBody || '').trim();
    if (!nextBlock) return currentBody;
    return currentBody ? `${nextBlock}\n\n---\n\n${currentBody}` : nextBlock;
}

function sanitizeFilenameStem(value) {
    const stem = String(value || '')
        .replace(/[\\/:*?"<>|]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 120);
    return stem;
}

function resolvePdfDisplayName({ metaData, message, pageUrl }) {
    const titleCandidates = [metaData?.title, message?.title];

    for (const candidate of titleCandidates) {
        const stem = sanitizeFilenameStem(candidate);
        if (stem) {
            return stem.toLowerCase().endsWith('.pdf') ? stem : `${stem}.pdf`;
        }
    }

    const parsed = parseUrl(pageUrl);
    if (parsed) {
        const host = parsed.hostname.replace(/^www\./i, '');
        const pathTail = parsed.pathname
            .split('/')
            .filter(Boolean)
            .slice(-3)
            .join('-');
        const stem = sanitizeFilenameStem([host, pathTail].filter(Boolean).join(' '));
        if (stem) {
            return `${stem}.pdf`;
        }
    }

    const dateLabel = new Date().toISOString().slice(0, 10);
    return `saved-page-${dateLabel}.pdf`;
}

function buildLinkMessageContent({ message, metaData, pageUrl }) {
    const fromMessageMeta = Array.isArray(message?.urlMeta)
        ? message.urlMeta.map(sanitizeUrlMetaInput).filter(Boolean)
        : [];
    const urlMeta = fromMessageMeta.length
        ? fromMessageMeta
        : (metaData ? [sanitizeUrlMetaInput(metaData)].filter(Boolean) : []);

    return {
        title: message?.title || metaData?.title || '',
        body: message?.body || metaData?.url || pageUrl || '',
        urlMeta,
        metadata: {
            savedFrom: message?.metadata?.savedFrom || {
                type: 'WEB',
                device: 'Macintosh',
                browser: 'Chrome',
                browserVersion: '140'
            }
        }
    };
}

const MediaToolsPanelContainer = React.forwardRef(function MediaToolsPanelContainer(
    { message, metaData, urlMetaIndex = 0, onUploadComplete, onVideoAnalysisStateChange, variant = 'light' },
    ref
) {
    const {t: lnT} = useTranslation();

    const [status, setStatus] = useState(null);
    const [showOverwriteModal, setShowOverwriteModal] = useState(false);
    const [isPullingLinkMarkdown, setIsPullingLinkMarkdown] = useState(false);
    const [isGeneratingPagePdf, setIsGeneratingPagePdf] = useState(false);
    const [mediaVariant, setMediaVariant] = useState('VIDEO');
    const [videoQuality, setVideoQuality] = useState('AUTO');
    const [audioQuality, setAudioQuality] = useState('AUTO');
    const [isDownloadCancelable, setIsDownloadCancelable] = useState(false);
    const activeDownloadAbortRef = useRef(null);
    const mediaToolsPanelRef = useRef(null);

    const videoUrl = useMemo(() => getUrlMetaVideoSource(metaData) || metaData?.videoUrl || null, [lnT, metaData]);
    const hlsUrl = useMemo(() => metaData?.media_hls_url || metaData?.hlsUrl || null, [lnT, metaData]);
    const hasLegacyVideoActions = useMemo(
        () => isUrlMetaVideo(metaData) && !!videoUrl,
        [lnT, metaData, videoUrl]
    );
    const hasSavedVideoTranscriptSource = useMemo(
        () => hasSavedVideoMedia(metaData),
        [lnT, metaData]
    );
    const pageUrl = useMemo(
        () => resolvePrimaryUrl({ metaData, message, urlMetaIndex }),
        [lnT, metaData, message, urlMetaIndex]
    );
    const supportedSocialUrl = useMemo(
        () => resolveSupportedSocialUrl(pageUrl),
        [lnT, pageUrl]
    );
    const articlePullBlockedForSocialPost = useMemo(
        () => isBlockedSocialArticleUrl(pageUrl),
        [lnT, pageUrl]
    );
    const ytDlpSupportedUrl = useMemo(
        () => resolveYtDlpSupportedUrl(pageUrl),
        [lnT, pageUrl]
    );
    const youtubeUrl = useMemo(
        () => resolveYouTubeUrl(pageUrl),
        [lnT, pageUrl]
    );
    const redditPostUrl = useMemo(
        () => resolveRedditPostUrl(pageUrl),
        [lnT, pageUrl]
    );

    const { data: userData } = useQuery(QUERY.ME, {
        errorPolicy: 'all'
    });

    const { data: ytDlpFlagData, loading: ytDlpFlagLoading } = useQuery(GET_FEATURE_FLAG, {
        variables: { name: URL_MEDIA_TOOLS_YTDLP_FLAG },
        errorPolicy: 'all'
    });
    const { data: videoTextScanFlagData, loading: videoTextScanFlagLoading } = useQuery(GET_FEATURE_FLAG, {
        variables: { name: URL_VIDEO_TEXT_SCAN_FLAG },
        errorPolicy: 'all'
    });
    const { data: transcriptionFlagData, loading: transcriptionFlagLoading } = useQuery(GET_FEATURE_FLAG, {
        variables: { name: URL_TRANSCRIPT_FLAG },
        errorPolicy: 'all'
    });
    const { data: redditJsonSaveFlagData, loading: redditJsonSaveFlagLoading } = useQuery(GET_FEATURE_FLAG, {
        variables: { name: REDDIT_JSON_SAVE_FLAG },
        errorPolicy: 'all'
    });

    const ytDlpFlag = ytDlpFlagData?.getFeatureFlag;
    const videoTextScanFlag = videoTextScanFlagData?.getFeatureFlag;
    const transcriptionFlag = transcriptionFlagData?.getFeatureFlag;
    const redditJsonSaveFlag = redditJsonSaveFlagData?.getFeatureFlag;
    const isYtDlpFeatureEnabled = evaluateFeatureFlagForUser(ytDlpFlag, userData?.me, {
        loading: ytDlpFlagLoading
    }).allowed;
    const isVideoTextScanFeatureEnabled = evaluateFeatureFlagForUser(videoTextScanFlag, userData?.me, {
        loading: videoTextScanFlagLoading
    }).allowed;
    const transcriptionAccessDecision = evaluateFeatureFlagForUser(transcriptionFlag, userData?.me, {
        loading: transcriptionFlagLoading
    });
    const isRedditJsonSaveFeatureEnabled = evaluateFeatureFlagForUser(redditJsonSaveFlag, userData?.me, {
        loading: redditJsonSaveFlagLoading
    }).allowed;
    const canUseYtDlpTools = Boolean(isYtDlpFeatureEnabled && ytDlpSupportedUrl);
    const canUseTranscriptTools = Boolean(isYtDlpFeatureEnabled && (ytDlpSupportedUrl || hasSavedVideoTranscriptSource));
    const canScanVideoText = Boolean(isVideoTextScanFeatureEnabled && canUseTranscriptTools);
    const videoTextScanStatus = String(message?.metadata?.videoFrameAnalysis?.status || '').toLowerCase();
    const hasCompletedVideoTextScan = videoTextScanStatus === 'completed';
    const canReanalyzeVideo = hasCompletedVideoTextScan || videoTextScanStatus === 'failed';
    const isVideoTextScanInBackground = videoTextScanStatus === 'pending' || videoTextScanStatus === 'processing';
    const youtubeTranscriptOnlyMode = Boolean(canUseYtDlpTools && youtubeUrl);
    const transcriptProcessing = message?.metadata?.audioProcessing;
    const isTranscriptProcessing = isAudioProcessingActive(transcriptProcessing);
    const transcriptProcessingStatus = getAudioProcessingStatusText(transcriptProcessing);
    const showDeveloperActions = userData?.me?.role === 'ADMIN';
    const canDownloadRedditJsonDump = Boolean(isRedditJsonSaveFeatureEnabled && redditPostUrl);
    const showVideoActions = hasLegacyVideoActions || canUseYtDlpTools || canUseTranscriptTools || canDownloadRedditJsonDump;

    useEffect(() => {
        onVideoAnalysisStateChange?.({
            canAnalyze: canScanVideoText,
            completed: hasCompletedVideoTextScan,
            canReanalyze: canReanalyzeVideo,
            processing: isVideoTextScanInBackground
        });
    }, [canReanalyzeVideo, canScanVideoText, hasCompletedVideoTextScan, isVideoTextScanInBackground, onVideoAnalysisStateChange]);

    const [uploadUrlMetaMedia, { loading: uploadingToMessage }] = useMutation(UPLOAD_URLMETA_MEDIA);
    const [uploadVideoFromUrl, { loading: uploadingToNewMessage }] = useMutation(UPLOAD_VIDEO_FROM_URL);
    const [extractUrlMediaMutation, { loading: extractingUrlMedia }] = useMutation(EXTRACT_URL_MEDIA);
    const [extractUrlTranscriptMutation, { loading: extractingTranscript }] = useMutation(EXTRACT_URL_TRANSCRIPT);
    const [scanUrlVideoTextMutation, { loading: scanningVideoText }] = useMutation(SCAN_URL_VIDEO_TEXT);
    const [downloadUrlInfoDumpMutation, { loading: downloadingUrlInfoDump }] = useMutation(DOWNLOAD_URL_INFO_DUMP);
    const [downloadRedditJsonDumpMutation, { loading: downloadingRedditJsonDump }] = useMutation(DOWNLOAD_REDDIT_JSON_DUMP);
    const [probeUrlDirectDownloadabilityMutation, { loading: probingDirectDownloadability }] = useMutation(PROBE_URL_DIRECT_DOWNLOADABILITY);
    const [createMessageMutation, { loading: creatingMessage }] = useMutation(CREATE_MESSAGE);
    const [pullUrlMarkdownMutation] = useMutation(PULL_URL_MARKDOWN);
    const [generateUrlPdfMutation] = useMutation(GENERATE_URL_PDF);
    const { downloadVideo, loading: downloadingVideo } = useDownloadUrlMetaVideo();
    const { updateMessage, updateURL } = useUpdateMessageMutation();
    const { showDefaultNotification } = useNotificationSystem();

    const notifyBackground = useCallback((title) => {
        if (!title) {
            return;
        }
        showDefaultNotification(createClientRequestId('media-tools-notification'), title);
    }, [lnT, showDefaultNotification]);

    const broadcastMessageUpdated = useCallback((updatedMessage) => {
        if (typeof window === 'undefined' || !updatedMessage?.id) {
            return;
        }
        window.dispatchEvent(
            new CustomEvent('messageUpdated', {
                detail: {
                    message: updatedMessage
                }
            })
        );
    }, [lnT]);

    const applyCurrentMessagePatch = useCallback((patch = {}) => {
        if (!message?.id || !patch || typeof patch !== 'object') {
            return null;
        }

        const updatedMessage = {
            ...message,
            ...patch,
            updatedAt: patch.updatedAt || new Date().toISOString()
        };

        try {
            Object.assign(message, updatedMessage);
        } catch (err) {
            console.error('Failed to apply local message patch', err);
        }

        broadcastMessageUpdated(updatedMessage);
        return updatedMessage;
    }, [lnT, message, broadcastMessageUpdated]);

    const buildUrlMetaMediaPatch = useCallback((uploadedFile, baseMessage = message, targetUrlMetaIndex = urlMetaIndex) => {
        if (!uploadedFile || !baseMessage) {
            return null;
        }

        const currentUrlMeta = Array.isArray(baseMessage.urlMeta) ? baseMessage.urlMeta : [];
        const nextUrlMeta = [...currentUrlMeta];
        const currentMeta = {
            ...(nextUrlMeta[targetUrlMetaIndex] || metaData || {})
        };

        const patchedMeta = {
            ...currentMeta,
            media_url: uploadedFile.url || currentMeta.media_url,
            media_name: uploadedFile.name || currentMeta.media_name,
            media_type: uploadedFile.type || currentMeta.media_type || 'video',
            media_asset_id: uploadedFile.shared_asset_id || currentMeta.media_asset_id
        };

        if (uploadedFile.cdn_url) {
            patchedMeta.media_cdn_url = uploadedFile.cdn_url;
        }
        if (uploadedFile.width) {
            patchedMeta.media_width = uploadedFile.width;
        }
        if (uploadedFile.height) {
            patchedMeta.media_height = uploadedFile.height;
        }
        if (uploadedFile.duration) {
            patchedMeta.media_duration = uploadedFile.duration;
        }

        nextUrlMeta[targetUrlMetaIndex] = patchedMeta;
        return { urlMeta: nextUrlMeta };
    }, [lnT, message, metaData, urlMetaIndex]);

    const handleUploadSuccess = useCallback((uploadedFile, { attachToUrlMeta = false } = {}) => {
        if (uploadedFile) {
            const patch = attachToUrlMeta ? buildUrlMetaMediaPatch(uploadedFile) : { file: uploadedFile };
            applyCurrentMessagePatch(patch || { file: uploadedFile });
            if (onUploadComplete) {
                onUploadComplete(uploadedFile);
            }
        }
    }, [lnT, applyCurrentMessagePatch, buildUrlMetaMediaPatch, onUploadComplete]);

    const appendErrorToMessageBody = useCallback(async (actionLabel, errorMessage) => {
        const action = String(actionLabel || '').trim();
        const messageText = sanitizeClientFacingLinkToolError(errorMessage, 'Operation failed');
        if (!messageText || !message?.id) {
            return;
        }

        const errorBlock = [
            '[Link Media Tools Error]',
            action ? `Action: ${action}` : null,
            `Message: ${messageText}`,
            `Time: ${new Date().toISOString()}`
        ].filter(Boolean).join('\n');

        const bodyToSave = prependBodyBlock(message?.body, errorBlock);
        const isLocalBookmark = String(message.id).startsWith('x-');
        const isTempMessage = String(message.id).startsWith('temp-');

        try {
            if (isLocalBookmark) {
                localStorage.setItem(`luckynote_local_body_${message.id}`, bodyToSave);
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(
                        new CustomEvent('localNoteUpdated', {
                            detail: {
                                messageId: message.id,
                                localBody: bodyToSave
                            }
                        })
                    );
                }
                return;
            }

            if (isTempMessage) {
                return;
            }

            await updateMessage(message, {
                title: message?.title || '',
                body: bodyToSave
            });
        } catch (updateErr) {
            console.error('Failed to append link tools error to message body', updateErr);
        }
    }, [lnT, message, updateMessage]);

    const extractUrlMedia = useCallback(async ({ messageId, target, abortSignal }) => {
        const context = abortSignal
            ? {
                fetchOptions: {
                    signal: abortSignal
                }
            }
            : undefined;

        const response = await extractUrlMediaMutation({
            variables: {
                data: {
                    messageId,
                    urlMetaIndex,
                    variant: mediaVariant,
                    target,
                    clientRequestId: createClientRequestId('extract-url-media'),
                    videoQuality,
                    audioQuality
                }
            },
            context
        });

        return response?.data?.extractUrlMedia;
    }, [lnT, extractUrlMediaMutation, urlMetaIndex, mediaVariant, videoQuality, audioQuality]);

    const handleUpload = useCallback(async () => {
        if (!message?.id) {
            setStatus(lnT('messages:cannotUploadMessageMissingId'));
            return;
        }
        if (message.file) {
            setShowOverwriteModal(true);
            return;
        }
        notifyBackground(`Saving ${mediaVariant === 'AUDIO' ? 'audio' : 'video'} in background...`);
        try {
            const uploadedFile = canUseYtDlpTools
                ? await extractUrlMedia({ messageId: message.id, target: 'MESSAGE' })
                : (await uploadUrlMetaMedia({
                    variables: {
                        data: {
                            messageId: message.id,
                            urlMetaIndex
                        }
                    }
                }))?.data?.uploadUrlMetaMedia;
            handleUploadSuccess(uploadedFile, { attachToUrlMeta: mediaVariant === 'VIDEO' });
            customAnalytics.message_action({
                action: canUseYtDlpTools ? 'Extract URL Media to Message' : 'Upload UrlMeta Video',
                type: 'Link'
            }, message);
            setStatus(lnT('messages:savedToMessageSuccessfully', { value1: String(mediaVariant === 'AUDIO' ? 'Audio' : 'Video') }));
            notifyBackground(`${mediaVariant === 'AUDIO' ? 'Audio' : 'Video'} saved to message.`);
        } catch (err) {
            const errorMessage = resolveActionErrorMessage(err, 'Media download failed');
            setStatus(lnT('messages:failedToSaveMedia', { value1: String(errorMessage) }));
            notifyBackground(`Failed to save media: ${errorMessage}`);
            await appendErrorToMessageBody('Save media to message', errorMessage);
        }
    }, [lnT, message, urlMetaIndex, uploadUrlMetaMedia, handleUploadSuccess, canUseYtDlpTools, extractUrlMedia, mediaVariant, appendErrorToMessageBody, notifyBackground]);

    const handleOverwrite = useCallback(async () => {
        if (!message?.id) {
            setStatus(lnT('messages:cannotOverwriteMessageMissingId'));
            return;
        }
        setShowOverwriteModal(false);
        notifyBackground(`Overwriting ${mediaVariant === 'AUDIO' ? 'audio' : 'video'} in background...`);
        try {
            const uploadedFile = canUseYtDlpTools
                ? await extractUrlMedia({ messageId: message.id, target: 'MESSAGE' })
                : (await uploadUrlMetaMedia({
                    variables: {
                        data: {
                            messageId: message.id,
                            urlMetaIndex
                        }
                    }
                }))?.data?.uploadUrlMetaMedia;
            handleUploadSuccess(uploadedFile, { attachToUrlMeta: mediaVariant === 'VIDEO' });
            customAnalytics.message_action({
                action: canUseYtDlpTools ? 'Overwrite URL Media' : 'Overwrite Video',
                type: 'Link'
            }, message);
            setStatus(lnT('messages:overwrittenSuccessfully', { value1: String(mediaVariant === 'AUDIO' ? 'Audio' : 'Video') }));
            notifyBackground(`${mediaVariant === 'AUDIO' ? 'Audio' : 'Video'} overwritten.`);
        } catch (err) {
            const errorMessage = resolveActionErrorMessage(err, 'Media download failed');
            setStatus(lnT('messages:failedToOverwriteMedia', { value1: String(errorMessage) }));
            notifyBackground(`Failed to overwrite media: ${errorMessage}`);
            await appendErrorToMessageBody('Overwrite media', errorMessage);
        }
    }, [lnT, message, urlMetaIndex, uploadUrlMetaMedia, handleUploadSuccess, canUseYtDlpTools, extractUrlMedia, mediaVariant, appendErrorToMessageBody, notifyBackground]);

    const uploadVideoToNewMessage = useCallback(async (newMessageId) => {
        if (!newMessageId) return;
        try {
            let uploadedFile = null;
            if (canUseYtDlpTools) {
                uploadedFile = await extractUrlMedia({ messageId: newMessageId, target: 'MESSAGE' });
            } else {
                uploadedFile = (await uploadVideoFromUrl({
                    variables: {
                        data: {
                            messageId: newMessageId,
                            videoUrl,
                            hlsUrl
                        }
                    }
                }))?.data?.uploadVideoFromUrl;
            }

            if (uploadedFile) {
                const baseNewMessage = {
                    urlMeta: metaData ? [sanitizeUrlMetaInput(metaData)].filter(Boolean) : []
                };
                const patch = canUseYtDlpTools && mediaVariant === 'VIDEO'
                    ? buildUrlMetaMediaPatch(uploadedFile, baseNewMessage, 0)
                    : { file: uploadedFile };

                broadcastMessageUpdated({
                    id: newMessageId,
                    folder: message?.folder || null,
                    ...patch,
                    updatedAt: new Date().toISOString()
                });
            }

            customAnalytics.message_action({
                action: canUseYtDlpTools ? 'Create New Message with URL Media' : 'Create New Message with Video',
                type: 'Link'
            }, message);
            setStatus(lnT('messages:savedToNewMessageSuccessfully', { value1: String(mediaVariant === 'AUDIO' ? 'Audio' : 'Video') }));
            notifyBackground(`${mediaVariant === 'AUDIO' ? 'Audio' : 'Video'} saved as a new message.`);
        } catch (err) {
            const errorMessage = resolveActionErrorMessage(err, 'Failed to save media to new message');
            setStatus(lnT('messages:failedToSaveMediaToNewMessage', { value1: String(errorMessage) }));
            notifyBackground(`Failed to save media to new message: ${errorMessage}`);
            await appendErrorToMessageBody('Save media as new message', errorMessage);
        }
    }, [lnT, uploadVideoFromUrl, videoUrl, hlsUrl, message, metaData, canUseYtDlpTools, extractUrlMedia, mediaVariant, buildUrlMetaMediaPatch, appendErrorToMessageBody, notifyBackground, broadcastMessageUpdated]);

    const handleSaveAsNewMessage = useCallback(async () => {
        if (!message?.folder?.id) {
            setStatus(lnT('messages:cannotCreateNewMessageMissingFolder'));
            return;
        }
        notifyBackground(`Creating new message and saving ${mediaVariant === 'AUDIO' ? 'audio' : 'video'} in background...`);
        try {
            const res = await createMessageMutation({
                variables: {
                    data: {
                        folderId: message.folder.id,
                        content: {
                            title: metaData?.title || '',
                            body: metaData?.url || '',
                            urlMeta: metaData ? [sanitizeUrlMetaInput(metaData)].filter(Boolean) : [],
                            metadata: {
                                savedFrom: message.metadata?.savedFrom || {
                                    type: 'WEB',
                                    device: 'Macintosh',
                                    browser: 'Chrome',
                                    browserVersion: '140'
                                }
                            }
                        }
                    }
                }
            });
            const newMessageId = res?.data?.createMessage?.id;
            setStatus(lnT('messages:newMessageCreatedUploading', { value1: String(mediaVariant === 'AUDIO' ? 'audio' : 'video') }));
            if (newMessageId) {
                await uploadVideoToNewMessage(newMessageId);
            }
        } catch (err) {
            const errorMessage = resolveActionErrorMessage(err, 'Failed to create new message');
            setStatus(lnT('messages:failedToCreateNewMessage', { value1: String(errorMessage) }));
            notifyBackground(`Failed to create new message: ${errorMessage}`);
            await appendErrorToMessageBody('Create new message for media', errorMessage);
        }
    }, [lnT, createMessageMutation, message, metaData, uploadVideoToNewMessage, mediaVariant, appendErrorToMessageBody, notifyBackground]);

    const isUploading = uploadingToMessage || uploadingToNewMessage || creatingMessage || extractingUrlMedia;
    const handleCancelDownload = useCallback(() => {
        const activeController = activeDownloadAbortRef.current;
        if (!activeController) {
            return;
        }

        setStatus(lnT('messages:cancellingDownload'));
        notifyBackground('Cancelling download...');
        activeController.abort();
        activeDownloadAbortRef.current = null;
        setIsDownloadCancelable(false);
    }, [lnT, notifyBackground]);

    const handleDownloadVideo = useCallback(async () => {
        const abortController = typeof AbortController !== 'undefined'
            ? new AbortController()
            : null;
        activeDownloadAbortRef.current = abortController;
        setIsDownloadCancelable(Boolean(abortController));

        notifyBackground(`Preparing ${mediaVariant === 'AUDIO' ? 'audio' : 'video'} download in background...`);
        try {
            if (canUseYtDlpTools) {
                return await extractUrlMedia({
                    messageId: message.id,
                    target: 'DOWNLOAD',
                    abortSignal: abortController?.signal
                });
            }
            return await downloadVideo({
                messageId: message.id,
                urlMetaIndex,
                abortSignal: abortController?.signal
            });
        } catch (err) {
            if (isAbortLikeError(err)) {
                setStatus(lnT('messages:downloadCancelled'));
                notifyBackground('Download cancelled.');
                throw new Error('Download cancelled');
            }

            const errorMessage = resolveActionErrorMessage(err, 'Download preparation failed');
            notifyBackground(`Failed to prepare download: ${errorMessage}`);
            await appendErrorToMessageBody('Download media', errorMessage);
            throw err;
        } finally {
            if (activeDownloadAbortRef.current === abortController) {
                activeDownloadAbortRef.current = null;
            }
            setIsDownloadCancelable(false);
        }
    }, [lnT, downloadVideo, message?.id, urlMetaIndex, canUseYtDlpTools, extractUrlMedia, appendErrorToMessageBody, mediaVariant, notifyBackground]);

    const handleExtractTranscript = useCallback(async ({ transcriptFormat: requestedFormat } = {}) => {
        if (!canUseTranscriptTools) {
            setStatus(lnT('messages:transcriptExtractionIsAvailableOnlyForSupported'));
            return null;
        }

        if (!message?.id) {
            setStatus(lnT('messages:cannotExtractTranscriptMessageMissingId'));
            return null;
        }

        if (isTranscriptProcessing) {
            const activeStatus = transcriptProcessingStatus || 'Getting transcript...';
            setStatus(activeStatus);
            notifyBackground(activeStatus);
            return null;
        }

        if (transcriptionAccessDecision?.requiresUpgrade) {
            setStatus(lnT('messages:openingUpgradeOptions_e109ae86'));
            openFeatureUpgradeModal({
                preset: FEATURE_UPGRADE_MODAL_PRESET.URL_TRANSCRIPT,
                upgradeType: 'personal',
                allowedPlanTypes: transcriptionFlag?.allowedPlanTypes
            });
            return null;
        }

        if (transcriptionAccessDecision?.requiresTeamUpgrade) {
            setStatus(lnT('messages:openingTeamUpgradeOptions_c575e41f'));
            openFeatureUpgradeModal({
                preset: FEATURE_UPGRADE_MODAL_PRESET.URL_TRANSCRIPT,
                upgradeType: 'team',
                allowedPlanTypes: transcriptionFlag?.allowedPlanTypes
            });
            return null;
        }

        if (!transcriptionAccessDecision?.allowed) {
            const fallbackMessage = (() => {
                switch (transcriptionAccessDecision?.reason) {
                case 'LOADING':
                    return 'Checking transcript availability. Please try again.';
                case 'FLAG_DISABLED':
                    return 'Transcript extraction is currently disabled';
                case 'TEAM_CONTEXT_REQUIRED':
                    return 'Transcript extraction requires an active team workspace context.';
                default:
                    return 'Transcript extraction is unavailable right now.';
                }
            })();
            setStatus(fallbackMessage);
            return null;
        }

        const selectedFormat = requestedFormat === 'TIMESTAMPED' ? 'TIMESTAMPED' : 'PLAIN';
        const isTimestamped = selectedFormat === 'TIMESTAMPED';
        const statusLabel = isTimestamped ? lnT('messages:timestampedTranscript') : lnT('messages:transcript_54e6289e');
        const actionLabel = isTimestamped ? lnT('messages:extractTimestampedTranscript_d64cf944') : lnT('messages:extractTranscript');
        const progressStatus = `Extracting ${statusLabel}...`;
        const noSubtitlesMessage = youtubeUrl
            ? 'No captions were found and automatic audio transcription could not complete. Please try again.'
            : 'No subtitles available for this link. Save audio to the message, then transcribe with the existing audio transcription flow.';

        notifyBackground(`Extracting ${statusLabel} in background...`);
        try {
            setStatus(progressStatus);
            const response = await extractUrlTranscriptMutation({
                variables: {
                    data: {
                        messageId: message.id,
                        urlMetaIndex,
                        format: selectedFormat,
                        allowProviderFallback: true
                    }
                }
            });

            const result = response?.data?.extractUrlTranscript;
            if (result?.__typename === 'ExtractUrlTranscriptError') {
                const uiAction = mapTranscriptionErrorToUiAction({
                    error: result,
                    defaultDecision: transcriptionAccessDecision
                });
                if (uiAction.openUpgradeModal) {
                    setStatus(lnT('messages:openingUpgradeOptions_e109ae86'));
                    openFeatureUpgradeModal({
                        preset: FEATURE_UPGRADE_MODAL_PRESET.URL_TRANSCRIPT,
                        upgradeType: 'personal',
                        allowedPlanTypes: transcriptionFlag?.allowedPlanTypes
                    });
                    return result;
                }
                if (uiAction.openTeamUpgradeModal) {
                    setStatus(lnT('messages:openingTeamUpgradeOptions_c575e41f'));
                    openFeatureUpgradeModal({
                        preset: FEATURE_UPGRADE_MODAL_PRESET.URL_TRANSCRIPT,
                        upgradeType: 'team',
                        allowedPlanTypes: transcriptionFlag?.allowedPlanTypes
                    });
                    return result;
                }
                if (result?.code === 'NO_SUBTITLES_AVAILABLE') {
                    setStatus(noSubtitlesMessage);
                    notifyBackground(noSubtitlesMessage);
                } else {
                    const transcriptErrorMessage = sanitizeClientFacingLinkToolError(
                        uiAction.showMessage || result?.message,
                        'Transcript extraction failed'
                    );
                    setStatus(transcriptErrorMessage);
                    notifyBackground(transcriptErrorMessage);
                }
                await appendErrorToMessageBody(
                    actionLabel,
                    sanitizeClientFacingLinkToolError(result?.message, 'Transcript extraction failed')
                );
                return result;
            }

            if (result?.id) {
                applyCurrentMessagePatch({
                    ...(typeof result?.body === 'string' ? { body: result.body } : {}),
                    ...(result?.transcript ? { transcript: result.transcript } : {}),
                    ...(result?.metadata ? { metadata: result.metadata } : {}),
                    ...(Array.isArray(result?.transcriptionSegments)
                        ? { transcriptionSegments: result.transcriptionSegments }
                        : {})
                });
            }

            customAnalytics.message_action({
                action: isTimestamped ? 'Extract URL Transcript Timestamped' : 'Extract URL Transcript',
                type: 'Link'
            }, message);
            const queued = ['pending', 'processing'].includes(String(result?.metadata?.audioProcessing?.status || '').toLowerCase());
            const resultMessage = queued
                ? lnT('common:transcriptQueuedWeLlAddItWhen')
                : isTimestamped ? lnT('messages:timestampedTranscriptAddedToNote') : lnT('messages:transcriptAddedToNote');
            setStatus(resultMessage);
            notifyBackground(resultMessage);
            return result;
        } catch (err) {
            const errorMessage = resolveActionErrorMessage(err, 'Transcript extraction failed');
            setStatus(lnT('messages:failedToExtract', { value1: String(statusLabel), value2: String(errorMessage) }));
            notifyBackground(`Failed to extract ${statusLabel}: ${errorMessage}`);
            await appendErrorToMessageBody(actionLabel, errorMessage);
            return null;
        }
    }, [lnT,
        canUseTranscriptTools,
        message,
        isTranscriptProcessing,
        transcriptProcessingStatus,
        transcriptionAccessDecision,
        extractUrlTranscriptMutation,
        urlMetaIndex,
        appendErrorToMessageBody,
        notifyBackground,
        applyCurrentMessagePatch,
        youtubeUrl
    ]);

    const handleScanVideoText = useCallback(async () => {
        if (!canScanVideoText) {
            const unavailableMessage = 'Video analysis is available only for saved or supported video links.';
            setStatus(unavailableMessage);
            notifyBackground(unavailableMessage);
            return null;
        }

        if (!message?.id) {
            const unavailableMessage = 'Cannot analyze video: message missing id.';
            setStatus(unavailableMessage);
            notifyBackground(unavailableMessage);
            return null;
        }

        const force = true;
        if (
            typeof window !== 'undefined' &&
            !window.confirm(lnT('messages:reanalyzeThisVideoThisRunsAFresh'))
        ) {
            return null;
        }

        const actionLabel = force ? lnT('messages:reanalyzingVideo') : lnT('messages:analyzingVideo');
        notifyBackground(`${actionLabel} in background...`);
        try {
            setStatus(lnT('messages:text', { value1: String(actionLabel) }));
            const response = await scanUrlVideoTextMutation({
                variables: {
                    data: {
                        messageId: message.id,
                        urlMetaIndex,
                        force
                    }
                }
            });

            const result = response?.data?.scanUrlVideoText;
            if (result?.id) {
                applyCurrentMessagePatch({
                    metadata: {
                        ...(message?.metadata || {}),
                        videoFrameAnalysis: {
                            ...(message?.metadata?.videoFrameAnalysis || {}),
                            status: 'pending',
                            operation: force ? 'url_video_text_scan_force_rerun' : 'url_video_text_scan',
                            startedAt: new Date().toISOString(),
                            error: null
                        }
                    }
                });
            }

            customAnalytics.message_action({ action: force ? 'Reanalyze Video' : 'Analyze Video', type: 'Link' }, message);
            setStatus(lnT('messages:started', { value1: String(actionLabel) }));
            notifyBackground(`${actionLabel} started.`);
            return result || true;
        } catch (err) {
            const errorMessage = resolveActionErrorMessage(err, 'Video analysis failed');
            setStatus(lnT('messages:failedToVideo', { value1: String(force ? 'reanalyze' : 'analyze'), value2: String(errorMessage) }));
            notifyBackground(`Failed to ${force ? 'reanalyze' : 'analyze'} video: ${errorMessage}`);
            await appendErrorToMessageBody(force ? 'Reanalyze video' : 'Analyze video', errorMessage);
            return null;
        }
    }, [lnT,
        canScanVideoText,
        message,
        scanUrlVideoTextMutation,
        urlMetaIndex,
        notifyBackground,
        applyCurrentMessagePatch,
        appendErrorToMessageBody
    ]);

    React.useImperativeHandle(ref, () => ({
        openToolsModal: () => mediaToolsPanelRef.current?.openToolsModal?.(),
        saveArticle: () => mediaToolsPanelRef.current?.saveArticle?.(),
        analyzeVideo: () => handleScanVideoText()
    }), [handleScanVideoText]);

    const handleDownloadUrlInfoDump = useCallback(async () => {
        if (!showDeveloperActions) {
            setStatus(lnT('messages:jsonDumpIsAvailableOnlyForAdmins'));
            return null;
        }

        if (!canUseYtDlpTools) {
            setStatus(lnT('messages:jsonDumpIsAvailableOnlyForSupported'));
            return null;
        }

        if (!message?.id) {
            setStatus(lnT('messages:cannotDownloadJsonDumpMessageMissingId'));
            return null;
        }

        notifyBackground('Preparing yt-dlp JSON dump in background...');
        try {
            setStatus(lnT('messages:preparingYtDlpJsonDump_6d415ea7'));
            const response = await downloadUrlInfoDumpMutation({
                variables: {
                    data: {
                        messageId: message.id,
                        urlMetaIndex
                    }
                }
            });

            const media = response?.data?.downloadUrlInfoDump;
            if (!media?.url) {
                throw new Error('JSON dump not available');
            }

            notifyBackground('yt-dlp JSON dump ready.');
            return media;
        } catch (err) {
            const errorMessage = resolveActionErrorMessage(err, 'JSON dump download failed');
            setStatus(lnT('messages:failedToPrepareJsonDump', { value1: String(errorMessage) }));
            notifyBackground(`Failed to prepare JSON dump: ${errorMessage}`);
            await appendErrorToMessageBody('Download yt-dlp JSON dump', errorMessage);
            throw err;
        }
    }, [lnT, showDeveloperActions, canUseYtDlpTools, message, downloadUrlInfoDumpMutation, urlMetaIndex, notifyBackground, appendErrorToMessageBody]);

    const handleDownloadRedditJsonDump = useCallback(async () => {
        if (!isRedditJsonSaveFeatureEnabled) {
            setStatus(lnT('messages:redditJsonSaveIsNotEnabledFor'));
            return null;
        }

        if (!redditPostUrl) {
            setStatus(lnT('messages:redditJsonSaveIsAvailableOnlyFor'));
            return null;
        }

        if (!message?.id) {
            setStatus(lnT('messages:cannotSaveRedditJsonMessageMissingId'));
            return null;
        }

        const isLocalBookmark = String(message.id).startsWith('x-');
        const isTempMessage = String(message.id).startsWith('temp-');
        if (isLocalBookmark || isTempMessage) {
            setStatus(lnT('messages:saveThisMessageFirstThenSaveReddit'));
            return null;
        }

        notifyBackground('Saving Reddit API JSON in background...');
        try {
            let targetMessageId = message.id;
            let savedAsNewMessage = false;

            if (message.file) {
                if (!message?.folder?.id) {
                    setStatus(lnT('messages:canTCreateANewMessageMissing'));
                    return null;
                }

                setStatus(lnT('messages:thisMessageAlreadyHasAFileCreating'));
                const content = buildLinkMessageContent({ message, metaData, pageUrl });
                const createRes = await createMessageMutation({
                    variables: {
                        data: {
                            folderId: message.folder.id,
                            content
                        }
                    }
                });
                const newMessageId = createRes?.data?.createMessage?.id;
                if (!newMessageId) {
                    throw new Error('failed to create message');
                }
                targetMessageId = newMessageId;
                savedAsNewMessage = true;
            }

            setStatus(lnT('messages:savingRedditApiJson_c8cfbf7a'));
            const response = await downloadRedditJsonDumpMutation({
                variables: {
                    data: {
                        messageId: targetMessageId,
                        urlMetaIndex
                    }
                }
            });

            const media = response?.data?.downloadRedditJsonDump;
            if (!media?.url) {
                throw new Error('Reddit JSON save failed');
            }

            if (!savedAsNewMessage) {
                handleUploadSuccess(media);
                setStatus(lnT('messages:redditJsonAddedToMessage'));
                notifyBackground('Reddit JSON saved to message.');
            } else {
                broadcastMessageUpdated({
                    id: targetMessageId,
                    folder: message?.folder || null,
                    file: media,
                    updatedAt: new Date().toISOString()
                });
                setStatus(lnT('messages:redditJsonSavedAsANewMessage'));
                notifyBackground('Reddit JSON saved as a new message.');
            }

            return media;
        } catch (err) {
            const errorMessage = resolveActionErrorMessage(err, 'Reddit JSON save failed');
            setStatus(lnT('messages:failedToSaveRedditJson', { value1: String(errorMessage) }));
            notifyBackground(`Failed to save Reddit JSON: ${errorMessage}`);
            await appendErrorToMessageBody('Save Reddit API JSON', errorMessage);
            throw err;
        }
    }, [lnT, isRedditJsonSaveFeatureEnabled, redditPostUrl, message, metaData, pageUrl, createMessageMutation, downloadRedditJsonDumpMutation, urlMetaIndex, notifyBackground, appendErrorToMessageBody, handleUploadSuccess, broadcastMessageUpdated]);

    const handleProbeUrlDirectDownloadability = useCallback(async () => {
        if (!showDeveloperActions) {
            setStatus(lnT('messages:directDownloadProbeIsAvailableOnlyFor'));
            return null;
        }

        if (!canUseYtDlpTools) {
            setStatus(lnT('messages:directDownloadProbeIsAvailableOnlyFor_af71d0a8'));
            return null;
        }

        if (!message?.id) {
            setStatus(lnT('messages:cannotProbeDirectDownloadsMessageMissingId'));
            return null;
        }

        notifyBackground('Preparing direct download probe in background...');
        try {
            setStatus(lnT('messages:preparingDirectDownloadProbe_0d9ecea9'));
            const response = await probeUrlDirectDownloadabilityMutation({
                variables: {
                    data: {
                        messageId: message.id,
                        urlMetaIndex
                    }
                }
            });

            const media = response?.data?.probeUrlDirectDownloadability;
            if (!media?.url) {
                throw new Error('Direct download probe not available');
            }

            notifyBackground('Direct download probe ready.');
            return media;
        } catch (err) {
            const errorMessage = resolveActionErrorMessage(err, 'Direct download probe failed');
            setStatus(lnT('messages:failedToPrepareDirectDownloadProbe', { value1: String(errorMessage) }));
            notifyBackground(`Failed to prepare direct download probe: ${errorMessage}`);
            await appendErrorToMessageBody('Probe direct downloadability', errorMessage);
            throw err;
        }
    }, [lnT, showDeveloperActions, canUseYtDlpTools, message, probeUrlDirectDownloadabilityMutation, urlMetaIndex, notifyBackground, appendErrorToMessageBody]);

    const handleSavePagePdf = useCallback(async () => {
        if (isGeneratingPagePdf) {
            return null;
        }

        if (!pageUrl) {
            setStatus(lnT('messages:noUrlFoundForPdf'));
            return null;
        }

        if (!message?.id) {
            setStatus(lnT('messages:canTGeneratePdfMissingMessageId'));
            return null;
        }

        const isLocalBookmark = String(message.id).startsWith('x-');
        const isTempMessage = String(message.id).startsWith('temp-');
        if (isLocalBookmark || isTempMessage) {
            setStatus(lnT('messages:saveThisMessageFirstThenCreateA'));
            return null;
        }

        const displayName = resolvePdfDisplayName({ metaData, message, pageUrl });
        setIsGeneratingPagePdf(true);
        notifyBackground('Saving page PDF in background...');
        try {
            let targetMessageId = message.id;
            let savedAsNewMessage = false;

            if (message.file) {
                if (!message?.folder?.id) {
                    setStatus(lnT('messages:canTCreateANewMessageMissing'));
                    return null;
                }

                setStatus(lnT('messages:thisMessageAlreadyHasAFileCreating'));
                const content = buildLinkMessageContent({ message, metaData, pageUrl });
                const createRes = await createMessageMutation({
                    variables: {
                        data: {
                            folderId: message.folder.id,
                            content
                        }
                    }
                });
                const newMessageId = createRes?.data?.createMessage?.id;
                if (!newMessageId) {
                    throw new Error('failed to create message');
                }
                targetMessageId = newMessageId;
                savedAsNewMessage = true;
            }

            setStatus(lnT('messages:generatingPdf'));
            const clientRequestId = createClientRequestId('url-pdf');

            const response = await generateUrlPdfMutation({
                variables: {
                    data: {
                        messageId: targetMessageId,
                        url: pageUrl,
                        displayName,
                        clientRequestId
                    }
                }
            });
            const pdfMedia = response?.data?.generateUrlPdf;
            if (!pdfMedia?.url) {
                throw new Error('received empty media response');
            }

            if (!savedAsNewMessage) {
                handleUploadSuccess(pdfMedia);
                setStatus(lnT('messages:pdfAddedToMessage'));
                notifyBackground('PDF saved to message.');
            } else {
                broadcastMessageUpdated({
                    id: targetMessageId,
                    folder: message?.folder || null,
                    file: pdfMedia,
                    updatedAt: new Date().toISOString()
                });
                setStatus(lnT('messages:pdfSavedAsANewMessage'));
                notifyBackground('PDF saved as a new message.');
            }

            customAnalytics.message_action({
                action: savedAsNewMessage ? 'Save Page PDF as New Message' : 'Save Page PDF',
                type: 'Link'
            }, message);
            return pdfMedia;
        } catch (err) {
            const errorMessage = resolveActionErrorMessage(err, 'Failed to create PDF');
            setStatus(lnT('messages:couldnTCreatePdf', { value1: String(errorMessage) }));
            notifyBackground(`Couldn't create PDF: ${errorMessage}`);
            await appendErrorToMessageBody('Save page as PDF', errorMessage);
            return null;
        } finally {
            setIsGeneratingPagePdf(false);
        }
    }, [lnT, isGeneratingPagePdf, pageUrl, message, metaData, createMessageMutation, generateUrlPdfMutation, handleUploadSuccess, appendErrorToMessageBody, notifyBackground, broadcastMessageUpdated]);

    const pullLinkMarkdownWithProvider = useCallback(async (provider) => {
        if (isPullingLinkMarkdown) {
            return;
        }

        if (!pageUrl) {
            setStatus(lnT('messages:noUrlFoundForThisMessage'));
            return;
        }

        if (!message?.id) {
            setStatus(lnT('messages:canTSaveArticleMissingMessageId'));
            return;
        }

        const isLocalBookmark = String(message.id).startsWith('x-');
        const isTempMessage = String(message.id).startsWith('temp-');
        if (!isLocalBookmark && isTempMessage) {
            setStatus(lnT('messages:saveThisMessageFirstThenSaveThe'));
            return;
        }

        setIsPullingLinkMarkdown(true);
        notifyBackground('Saving article in background...');
        try {
            setStatus(lnT('messages:savingArticle'));
            const clientRequestId = createClientRequestId(`url-markdown-${provider}`);
            const response = await pullUrlMarkdownMutation({
                variables: {
                    data: {
                        url: pageUrl,
                        provider,
                        clientRequestId
                    }
                }
            });
            const markdown = String(response?.data?.pullUrlMarkdown || '').trim();
            if (!markdown) {
                throw new Error('received empty markdown');
            }

            if (isLocalBookmark) {
                throw new Error('save this bookmark first');
            } else {
                const nextUrlMeta = buildUrlMetaWithArticleBody({
                    message,
                    metaData,
                    urlMetaIndex,
                    pageUrl,
                    markdown,
                    provider
                });
                const updated = await updateURL(message, nextUrlMeta);
                if (!updated) {
                    throw new Error('message update failed');
                }
            }

            customAnalytics.message_action({
                action: 'Pull URL Markdown',
                type: 'Link'
            }, message);
            setStatus(lnT('messages:articleSavedForReading'));
            notifyBackground('Article saved for reading.');
        } catch (err) {
            const errorMessage = resolveActionErrorMessage(err, 'Failed to save article');
            setStatus(lnT('messages:couldnTSaveArticle', { value1: String(errorMessage) }));
            notifyBackground(`Couldn't save article: ${errorMessage}`);
            await appendErrorToMessageBody('Save article', errorMessage);
        } finally {
            setIsPullingLinkMarkdown(false);
        }
    }, [lnT, isPullingLinkMarkdown, pageUrl, message, metaData, urlMetaIndex, updateURL, pullUrlMarkdownMutation, appendErrorToMessageBody, notifyBackground]);

    const handlePullLinkMarkdownPrimary = useCallback(async () => {
        return pullLinkMarkdownWithProvider(MARKDOWN_PROVIDERS.MICROLINK);
    }, [lnT, pullLinkMarkdownWithProvider]);

    if (!showVideoActions && !supportedSocialUrl && !pageUrl) {
        return null;
    }

    return (
        <MediaToolsPanel
            ref={mediaToolsPanelRef}
            message={message}
            metaData={metaData}
            urlMetaIndex={urlMetaIndex}
            onUpload={handleUpload}
            onOverwrite={handleOverwrite}
            onSaveAsNewMessage={handleSaveAsNewMessage}
            onDownloadVideo={handleDownloadVideo}
            onExtractTranscript={handleExtractTranscript}
            onScanVideoText={canScanVideoText && canReanalyzeVideo ? handleScanVideoText : null}
            onDownloadUrlInfoDump={handleDownloadUrlInfoDump}
            onDownloadRedditJsonDump={handleDownloadRedditJsonDump}
            onProbeUrlDirectDownloadability={handleProbeUrlDirectDownloadability}
            onPullLinkMarkdown={handlePullLinkMarkdownPrimary}
            onSavePagePdf={handleSavePagePdf}
            mediaVariant={mediaVariant}
            setMediaVariant={setMediaVariant}
            videoQuality={videoQuality}
            setVideoQuality={setVideoQuality}
            audioQuality={audioQuality}
            setAudioQuality={setAudioQuality}
            status={transcriptProcessingStatus || status}
            setStatus={setStatus}
            isUploading={!!isUploading}
            isPreparingDownload={downloadingVideo || extractingUrlMedia}
            isExtractingTranscript={extractingTranscript || isTranscriptProcessing}
            isScanningVideoText={scanningVideoText}
            isVideoTextScanInBackground={isVideoTextScanInBackground}
            isTranscriptProcessingInBackground={isTranscriptProcessing && !extractingTranscript}
            isDownloadingUrlInfoDump={downloadingUrlInfoDump}
            isDownloadingRedditJsonDump={downloadingRedditJsonDump}
            isProbingDirectDownloadability={probingDirectDownloadability}
            isPullingLinkMarkdown={isPullingLinkMarkdown}
            canPullLinkMarkdown={!!pageUrl && !articlePullBlockedForSocialPost}
            isGeneratingPagePdf={isGeneratingPagePdf}
            canSavePagePdf={!!pageUrl}
            showVideoActions={showVideoActions}
            showYtDlpControls={canUseYtDlpTools}
            showTranscriptControls={canUseTranscriptTools}
            transcriptOnlyMode={youtubeTranscriptOnlyMode}
            showDeveloperActions={showDeveloperActions}
            showRedditJsonAction={canDownloadRedditJsonDump}
            canCancelDownload={isDownloadCancelable}
            onCancelDownload={handleCancelDownload}
            showOverwriteModal={showOverwriteModal}
            setShowOverwriteModal={setShowOverwriteModal}
            variant={variant}
        />
    );
});

MediaToolsPanelContainer.displayName = 'MediaToolsPanelContainer';

export default MediaToolsPanelContainer;
